import React, { useEffect, useState } from "react";
import { createGlobalStyle, ThemeProvider } from "styled-components";
import { HashRouter as Router, Route, Routes } from "react-router-dom";
import Home from "./containers/home";
import PrivacyPolicy from "./containers/privacy-policy";

const sharedTheme = {
  mobileBreakpoint: "900px",
};

const lightTheme = {
  ...sharedTheme,
  colorScheme: "light",
  colors: {
    themeColor: "#f7eee5",
    ink: "#1d2757",
    muted: "#5c668a",
    accent: "#2cb8e6",
    accentDark: "#ee5670",
    navy: "#453e9f",
    gold: "#f26c8a",
    green: "#9d88ff",
    surface: "#f7f7ff",
    surfaceMuted: "#ebeafd",
    border: "rgba(69, 62, 159, 0.12)",
    shadow: "0 30px 70px rgba(82, 77, 166, 0.14)",
    cardShadow: "0 18px 34px rgba(82, 77, 166, 0.1)",
    navSurface: "rgba(255, 255, 255, 0.78)",
    cardSurface: "rgba(255, 255, 255, 0.8)",
    cardBorderStrong: "rgba(255, 255, 255, 0.8)",
    previewFrameStart: "#ece9ff",
    previewFrameEnd: "#ffffff",
    previewFrameBorder: "rgba(69, 62, 159, 0.08)",
    backgroundBase: "#f7eee5",
    secondaryButtonBackground: "rgba(255, 255, 255, 0.94)",
    secondaryButtonHoverShadow: "0 16px 28px rgba(82, 77, 166, 0.12)",
    bottomPanelStart: "#5b66ac",
    bottomPanelEnd: "#3579a8",
    bottomPanelShadow: "0 24px 46px rgba(104, 80, 172, 0.24)",
    primaryButtonStart: "#5863ad",
    primaryButtonEnd: "#317cad",
    primaryButtonHoverStart: "#525ca4",
    primaryButtonHoverEnd: "#2c739f",
  },
};

const darkTheme = {
  ...sharedTheme,
  colorScheme: "dark",
  colors: {
    themeColor: "#0f1a2b",
    ink: "#eef2ff",
    muted: "#aeb6e5",
    accent: "#4cc7f2",
    accentDark: "#ff7b9d",
    navy: "#171639",
    gold: "#ff93b1",
    green: "#9b8cff",
    surface: "#15142f",
    surfaceMuted: "#201c45",
    border: "rgba(159, 154, 255, 0.22)",
    shadow: "0 30px 70px rgba(8, 7, 26, 0.44)",
    cardShadow: "0 18px 34px rgba(8, 7, 26, 0.28)",
    navSurface: "rgba(24, 22, 58, 0.88)",
    cardSurface: "rgba(24, 22, 58, 0.82)",
    cardBorderStrong: "rgba(126, 136, 255, 0.2)",
    previewFrameStart: "#242056",
    previewFrameEnd: "#15142f",
    previewFrameBorder: "rgba(126, 136, 255, 0.18)",
    backgroundBase: "#0f1a2b",
    secondaryButtonBackground: "rgba(24, 22, 58, 0.72)",
    secondaryButtonHoverShadow: "0 16px 28px rgba(8, 7, 26, 0.32)",
    bottomPanelStart: "#2b2d63",
    bottomPanelEnd: "#2f6784",
    bottomPanelShadow: "0 24px 46px rgba(28, 22, 72, 0.4)",
    primaryButtonStart: "#4f5eaa",
    primaryButtonEnd: "#39789a",
    primaryButtonHoverStart: "#49579f",
    primaryButtonHoverEnd: "#336c8b",
  },
};

function getSystemPrefersDark() {
  if (typeof window === "undefined" || !window.matchMedia) {
    return false;
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export default function App() {
  const [manualTheme, setManualTheme] = useState(null);
  const [systemPrefersDark, setSystemPrefersDark] = useState(getSystemPrefersDark);
  const resolvedTheme = manualTheme || (systemPrefersDark ? "dark" : "light");
  const theme = resolvedTheme === "dark" ? darkTheme : lightTheme;

  useEffect(() => {
    if (!window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (event) => {
      setSystemPrefersDark(event.matches);
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  useEffect(() => {
    document.body.setAttribute("data-theme", resolvedTheme);
  }, [resolvedTheme]);

  useEffect(() => {
    let themeColorMeta = document.querySelector('meta[name="theme-color"]:not([media])');

    if (!themeColorMeta) {
      themeColorMeta = document.createElement("meta");
      themeColorMeta.setAttribute("name", "theme-color");
      document.head.appendChild(themeColorMeta);
    }

    themeColorMeta.setAttribute("content", theme.colors.themeColor);
  }, [theme.colors.themeColor]);

  const toggleTheme = () => {
    setManualTheme((currentTheme) => {
      const activeTheme = currentTheme || (systemPrefersDark ? "dark" : "light");
      return activeTheme === "dark" ? "light" : "dark";
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          <Route
            path="/"
            element={
              <Home
                isDarkTheme={resolvedTheme === "dark"}
                onToggleTheme={toggleTheme}
              />
            }
          />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        </Routes>
        <GlobalStyle />
      </Router>
    </ThemeProvider>
  );
}

const GlobalStyle = createGlobalStyle`
  :root {
    color-scheme: ${({ theme }) => theme.colorScheme};
  }

  html,
  body {
    margin: 0;
    padding: 0;
    min-height: 100%;
  }

  * {
    box-sizing: border-box;
  }

  html {
    background: ${({ theme }) => theme.colors.backgroundBase};
  }

  body {
    font-family: "Manrope", "Segoe UI", sans-serif;
    color: ${({ theme }) => theme.colors.ink};
    background: ${({ theme }) => theme.colors.backgroundBase};
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  #root {
    min-height: 100vh;
  }

  *:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.accentDark};
    outline-offset: 3px;
  }

  h1,
  h2,
  h3,
  h4,
  button,
  a {
    font-family: "Space Grotesk", "Segoe UI", sans-serif;
  }

  a {
    color: inherit;
    text-decoration: none;
  }
`;
